<comp-paged-table
  [dataSource]="dataSource()"
  [columns]="columnsToShow()"
  [modelObjectProperty]="'model.finding'"
  [filteringSupport]="filteringSupport()"
>
  <ng-container matColumnDef="id">
    <th mat-header-cell *matHeaderCellDef>
      {{ 'model.finding.properties.id' | translate }}
    </th>
    <td mat-cell *matCellDef="let finding">
      {{ finding.id }}
    </td>
  </ng-container>

  <ng-container matColumnDef="evidenceId">
    <th mat-header-cell *matHeaderCellDef>
      {{ 'model.finding.properties.evidenceId' | translate }}
    </th>
    <td mat-cell *matCellDef="let finding">
      {{ finding.evidenceId }}
    </td>
  </ng-container>

  <ng-container matColumnDef="name">
    <th mat-header-cell *matHeaderCellDef mat-sort-header disableClear>
      {{ 'model.finding.properties.name' | translate }}
    </th>
    <td mat-cell *matCellDef="let finding" class="findings-expandable-cell">
      <comp-finding-name [finding]="finding"></comp-finding-name>
      <button
        type="button"
        comp-button="tertiary"
        [iconOnly]="true"
        class="findings-toggle-button"
        (click)="openDescriptionDialog(finding)"
        [title]="'general.moreInfo' | translate"
        [attr.aria-label]="'model.finding.openFindingDescriptionDialog' | translate"
      >
        <i comp-icon>open_in_full</i>
      </button>
    </td>
  </ng-container>

  <ng-container matColumnDef="description">
    <th mat-header-cell *matHeaderCellDef>
      {{ 'model.finding.properties.description' | translate }}
    </th>
    <td mat-cell *matCellDef="let finding" class="findings-expandable-cell">
      <div
        class="description-markdown"
        [class.description-markdown-expanded]="isDescriptionExpanded(finding.id)"
      >
        <markdown [data]="finding.description ?? ''"></markdown>
      </div>

      <button
        type="button"
        comp-button="tertiary"
        [iconOnly]="true"
        class="findings-toggle-button"
        (click)="openDescriptionDialog(finding)"
        [title]="'general.moreInfo' | translate"
        [attr.aria-label]="'model.finding.openFindingDescriptionDialog' | translate"
      >
        <i comp-icon>open_in_full</i>
      </button>
    </td>
  </ng-container>

  <ng-container matColumnDef="severity">
    <!--
    The default sort order for findings is "by severity, descending".
    See com.ul.sis.companion.webapp.api.convert.ComparatorConfiguration
    However, you have to set start="asc" here, because the "start" attribute sets the sort order
    that is applied *after* the first click.
    -->
    <th mat-header-cell *matHeaderCellDef mat-sort-header disableClear start="asc">
      {{ 'model.finding.properties.severity' | translate }}
    </th>
    <td mat-cell *matCellDef="let finding">
      <comp-severity-tag [severity]="finding.severity!"></comp-severity-tag>
    </td>
  </ng-container>

  <ng-container matColumnDef="status">
    <th mat-header-cell *matHeaderCellDef>
      {{ 'model.finding.properties.status' | translate }}
    </th>
    <td mat-cell class="finding-state" *matCellDef="let finding">
      <comp-finding-state
        [finding]="finding"
        (findingChange)="onFindingStatusChange(finding, $event)"
      ></comp-finding-state>
    </td>
  </ng-container>

  <ng-container matColumnDef="dependencyTrackKEV">
    <th mat-header-cell *matHeaderCellDef>
      {{ 'model.finding.properties.dependencyTrackKEV' | translate }}
    </th>

    <td mat-cell *matCellDef="let finding">
      @let kev = finding.kev;

      @if (kev !== undefined) {
        @if (kev) {
          <!--        @if (!dependencyTrackData.isKev && finding.id % 2 === 0) {-->
          <span class="kev" translate="general.yes"></span>
        } @else {
          <span class="no-kev" translate="general.no"></span>
        }
      }
    </td>
  </ng-container>

  <ng-container matColumnDef="dependencyTrackComponentName">
    <th mat-header-cell *matHeaderCellDef>
      {{ 'model.finding.properties.dependencyTrackComponentName' | translate }}
    </th>

    <td mat-cell *matCellDef="let finding">
      {{ getDependencyTrackVulnerabilityData(finding)?.componentName }}
    </td>
  </ng-container>

  <ng-container matColumnDef="dependencyTrackComponentVersion">
    <th mat-header-cell *matHeaderCellDef>
      {{ 'model.finding.properties.dependencyTrackComponentVersion' | translate }}
    </th>

    <td mat-cell *matCellDef="let finding">
      {{ getDependencyTrackVulnerabilityData(finding)?.componentVersion }}
    </td>
  </ng-container>

  <ng-container matColumnDef="dependencyTrackVulnerabilitySource">
    <th mat-header-cell *matHeaderCellDef>
      {{ 'model.finding.properties.dependencyTrackVulnerabilitySource' | translate }}
    </th>

    <td mat-cell *matCellDef="let finding">
      {{ getDependencyTrackVulnerabilityData(finding)?.vulnerabilitySource }}
    </td>
  </ng-container>

  <ng-container matColumnDef="dependencyTrackCvssV3BaseScore">
    <th mat-header-cell *matHeaderCellDef>
      {{ 'model.finding.properties.dependencyTrackCvssV3BaseScore' | translate }}
    </th>

    <td mat-cell *matCellDef="let finding">
      {{ getDependencyTrackVulnerabilityData(finding)?.cvssV3BaseScore }}
    </td>
  </ng-container>

  <ng-container matColumnDef="dependencyTrackEpssScore">
    <th mat-header-cell *matHeaderCellDef>
      {{ 'model.finding.properties.dependencyTrackEpssScore' | translate }}
    </th>

    <td mat-cell *matCellDef="let finding">
      {{ getDependencyTrackVulnerabilityData(finding)?.epssScore | percent: '1.0-3' }}
    </td>
  </ng-container>

  <ng-container matColumnDef="dependencyTrackEpssPercentile">
    <th mat-header-cell *matHeaderCellDef>
      {{ 'model.finding.properties.dependencyTrackEpssPercentile' | translate }}
    </th>

    <td mat-cell *matCellDef="let finding">
      {{ getDependencyTrackVulnerabilityData(finding)?.epssPercentile | percent: '1.0-3' }}
    </td>
  </ng-container>

  <ng-container matColumnDef="dependencyTrackCwes">
    <th mat-header-cell *matHeaderCellDef>
      {{ 'model.finding.properties.dependencyTrackCwes' | translate }}
    </th>

    <td mat-cell *matCellDef="let finding">
      <comp-finding-cwes [finding]="finding"></comp-finding-cwes>
    </td>
  </ng-container>

  <ng-container matColumnDef="dependencyTrackSuppressed">
    <th mat-header-cell *matHeaderCellDef>
      {{ 'model.finding.properties.dependencyTrackSuppressed' | translate }}
    </th>

    <td mat-cell *matCellDef="let finding">
      @let dependencyTrackData = getDependencyTrackVulnerabilityData(finding);

      @if (dependencyTrackData) {
        {{ dependencyTrackData.isSuppressed ? 'Yes' : 'No' }}
      }
    </td>
  </ng-container>

  <ng-container matColumnDef="dependencyTrackPublished">
    <th mat-header-cell *matHeaderCellDef>
      {{ 'model.finding.properties.dependencyTrackPublished' | translate }}
    </th>

    <td mat-cell *matCellDef="let finding">
      @let published = getDependencyTrackVulnerabilityData(finding)?.published;

      @if (published) {
        {{ published | date: 'shortDate' }}
      }
    </td>
  </ng-container>

  <ng-container matColumnDef="type">
    <th mat-header-cell *matHeaderCellDef>
      {{ 'model.finding.properties.type' | translate }}
    </th>
    <td mat-cell *matCellDef="let finding">
      {{ finding.type }}
    </td>
  </ng-container>

  <ng-container matColumnDef="data">
    <th mat-header-cell *matHeaderCellDef>
      {{ 'model.finding.properties.data' | translate }}
    </th>
    <td mat-cell *matCellDef="let finding" class="findings-expandable-cell">
      <pre class="finding-json" [class.finding-json-expanded]="isDataExpanded(finding.id)">{{
        prettyPrintJson(finding.data)
      }}</pre>

      @if (finding.data) {
        <button
          type="button"
          comp-button="tertiary"
          [iconOnly]="true"
          class="findings-toggle-button"
          (click)="toggleData(finding.id)"
          [attr.aria-expanded]="isDataExpanded(finding.id)"
        >
          <i comp-icon>
            {{ isDataExpanded(finding.id) ? 'expand_less' : 'expand_more' }}
          </i>
        </button>
      }
    </td>
  </ng-container>

  <ng-container matColumnDef="filename">
    <th mat-header-cell *matHeaderCellDef mat-sort-header disableClear>
      {{ 'model.finding.properties.filename' | translate }}
    </th>
    <td mat-cell *matCellDef="let finding">
      {{ finding.filename }}
    </td>
  </ng-container>

  <ng-container matColumnDef="createdAt">
    <th mat-header-cell *matHeaderCellDef mat-sort-header disableClear>
      {{ 'model.base.properties.createdAt' | translate }}
    </th>
    <td mat-cell *matCellDef="let finding">
      {{ finding.createdAt | date: 'short' }}
    </td>
  </ng-container>

  <ng-container matColumnDef="updatedAt">
    <th mat-header-cell *matHeaderCellDef>
      {{ 'model.base.properties.updatedAt' | translate }}
    </th>
    <td mat-cell *matCellDef="let finding">
      {{ finding.updatedAt | date: 'short' }}
    </td>
  </ng-container>

  <ng-container matColumnDef="createdBy">
    <th mat-header-cell *matHeaderCellDef>
      {{ 'model.base.properties.createdBy' | translate }}
    </th>
    <td mat-cell *matCellDef="let finding">
      {{ finding.createdBy }}
    </td>
  </ng-container>

  <ng-container matColumnDef="updatedBy">
    <th mat-header-cell *matHeaderCellDef>
      {{ 'model.base.properties.updatedBy' | translate }}
    </th>
    <td mat-cell *matCellDef="let finding">
      {{ finding.updatedBy }}
    </td>
  </ng-container>

  <ng-container matColumnDef="createdFrom">
    <th mat-header-cell *matHeaderCellDef>
      {{ 'model.base.properties.createdFrom' | translate }}
    </th>
    <td mat-cell *matCellDef="let finding">
      {{ finding.createdFrom }}
    </td>
  </ng-container>

  <ng-container matColumnDef="updatedFrom">
    <th mat-header-cell *matHeaderCellDef>
      {{ 'model.base.properties.updatedFrom' | translate }}
    </th>
    <td mat-cell *matCellDef="let finding">
      {{ finding.updatedFrom }}
    </td>
  </ng-container>
</comp-paged-table>
